<template>
  <div>
    <v-card class="pa-2" outlined tile height="100%">
      <v-data-table
        :headers="headers"
        :items="medicalVisits"
        sort-by="Date"
        sort-desc
        :loading="isLoading"
        loading-text="Loading Medical Visit Forms"
        :search="search"
        height="550"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-toolbar-title>Medical Visits</v-toolbar-title>
            <v-divider class="mx-4" inset vertical></v-divider>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              label="Search"
              clearable
              flat
              solo-inverted
              hide-details
              prepend-inner-icon="search"
              class="desktop"
            ></v-text-field>
            <v-spacer></v-spacer>
            <template v-if="!$vuetify.breakpoint.smAndDown">
              <v-menu offset-y>
                <template v-slot:activator="{ attrs, on }">
                  <v-btn
                    class="white--text mb-2 mr-3"
                    v-bind="attrs"
                    v-on="on"
                    color="blue-grey"
                    :loading="actionInProgress"
                    :disabled="actionInProgress"
                  >
                    actions
                    <v-icon right dark> mdi-tray-arrow-up</v-icon>
                  </v-btn>
                </template>

                <v-list class="pa-0">
                  <v-list-item
                    v-for="(item, index) in actions"
                    :key="index"
                    link
                  >
                    <v-list-item-title
                      v-text="item"
                      @click="onSelectAction(item)"
                    ></v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-toolbar>
          <v-text-field
            v-model="search"
            clearable
            flat
            solo-inverted
            hide-details
            prepend-inner-icon="search"
            label="search"
            class="mobile ml-5 mr-5 mb-3 mt-3"
          ></v-text-field>
        </template>
        <v-spacer></v-spacer>

        <template v-slot:item.actions="{ item }">
          <v-icon
            small
            class="hidden-sm-and-down mr-2"
            @click="downloadDocument(item)"
            v-if="userRole && item.documents.length > 0"
          >
            mdi-download
          </v-icon>
          <v-btn
            x-small
            fab
            outlined
            class="hidden-sm-and-up mr-3"
            @click="downloadDocument(item)"
            v-if="userRole && item.documents.length > 0"
          >
            <v-icon> mdi-download </v-icon>
          </v-btn>

          <v-icon
            small
            class="mr-2 hidden-sm-and-down"
            @click="onViewItemDialog(item)"
          >
            mdi-eye
          </v-icon>
          <v-btn
            small
            outlined
            color="light-blue darken-4"
            class="mr-3 hidden-sm-and-up"
            @click="onViewItemDialog(item)"
          >
            view
          </v-btn>

          <v-icon
            small
            class="mr-2 hidden-sm-and-down"
            @click="onDeleteItem(item)"
            v-if="userRole && !isAuditorRole"
          >
            mdi-delete
          </v-icon>
          <v-btn
            small
            outlined
            class="mr-2 hidden-sm-and-up red--text"
            @click="onDeleteItem(item)"
            v-if="userRole && !isAuditorRole"
          >
            delete
          </v-btn>
        </template>

        <template v-slot:no-data>
          <v-btn color="#2b3990" class="white--text" @click="getVisits()"
            >Reset
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <div id="print">
      <report-header>
        <h2>Medical Visits</h2>
      </report-header>
      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="printHeader"
            :items="medicalVisits"
            sort-by="Date"
            sort-desc
            class="elevation-1"
            :loading="isLoading"
            loading-text="Loading Medical Visits"
            :items-per-page="-1"
          >
          </v-data-table>
        </v-col>
      </v-row>
    </div>

    <OptionMenu :onAction="handleAction" :showActions="showActions" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ReportHeader from "@/components/Report/ReportHeader.vue";
import download from "downloadjs";
import { mapActions } from "vuex";
import reports from "@/mixins/reports";

export default {
  components: {
    ReportHeader,
  },
  props: {
    visits: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    userRole: {
      type: Boolean,
      default: false,
    },
    isAuditorRole: {
      type: Boolean,
      default: false,
    }
  },
  mixins: [reports],
  data() {
    return {
      headers: [
        {
          text: "Date of Visit",
          align: "start",
          sortable: true,
          value: "Date",
        },
        { text: "Visit Type", value: "VisitType" },
        { text: "Reviewing Provider", value: "ReviewingProvider.Name" },
        { text: "Annual Visit", value: "AnnualVisit" },
        { text: "Signature", value: "Signature" },
        { text: "Med Change", value: "MedChange" },
        { text: "Actions", value: "actions", width: "15%", sortable: false },
      ],
      search: "",
      showActions: {
        add: false,
        print: true,
        download: true,
      },
      actions: ["Print", "Download"],
      actionInProgress: false,
    };
  },
  computed: {
    ...mapGetters({
      clientData: "clients/clientData",
    }),

    medicalVisits() {
      return this.visits.map((visit) => {
        visit.Date =
          !visit.Date || visit.Date === "Invalid date" ? "" : visit.Date;
        return visit;
      });
    },
    printHeader() {
      return this.headers.slice(0, this.headers.length - 1);
    },
  },
  methods: {
    ...mapActions({
      generateDynamicReport: "reports/generateDynamicReport",
    }),

    onShowDialog() {
      let payload = {
        show: true,
        newItem: true,
      };
      this.$emit("on-show-dialog", payload);
    },

    onViewItemDialog(item) {
      let payload = {
        show: true,
        newItem: false,
        data: item,
      };
      this.$emit("on-show-dialog", payload);
    },

    downloadDocument(item) {
      if (item.documents.length > 0) {
        for (let i = 0; i < item.documents.length; i++) {
          let fileExt = item.documents[i].Name.split(".")[1];
          let fileDate = item.Date.split(" ")[0];
          let newFilename = `${this.clientData.Name} (${fileDate}) ${item.VisitType}`;
          this.download(item.documents[i].AWSLink, `${newFilename}.${fileExt}`);
        }
      }
    },

    getVisits() {
      this.$emit("get-visits");
    },

    onDeleteItem(item) {
      this.$emit("on-delete-item", item);
    },
    async download(documentLink, documentName) {

      const url = documentLink;
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        download(blob, documentName);
      } catch (error) {
        console.error("Download failed:", error);
      }

      // deprecated code: cause cross-origin issue
      // let FileSaver = require("file-saver");
      // FileSaver.saveAs(documentLink, documentName);
    },
    printTable() {
      this.handleAction("print");
    },

    async onSelectAction(action) {
      try {
        this.actionInProgress = true;
        let data = {
          query: {
            name: this.clientData.Name,
            clientId: this.clientData._id,
            generateFile: true,
          },
          endpoints: `medicalVisits/reports/items`,
          dynamic: true,
        };

        if (action === "Print") {
          await this.getReport(data);
          this.actionInProgress = false;
        } else {
          const response = await this.generateDynamicReport(data);
          download(
            response.data,
            `${this.clientData.Name}-medical-visits.pdf`,
            "application/pdf"
          );
          this.actionInProgress = false;
        }
      } catch (e) {
        this.msg = true;
        this.text = "An error occured";
        this.actionInProgress = false;
      }
    },

    handleAction(action) {
      switch (action) {
        case "print":
          this.onSelectAction("Print");
          break;
        case "download":
          this.onSelectAction("Download");
          break;
        default:
          console.error(`Unknown action: ${action}`);
      }
    },
  },
};
</script>
<style>
#print {
  display: none;
}

@media print {
  #print {
    display: block;
    border: none;
    width: 1200px;
    padding-right: 300px;
    margin-left: -200px;
  }

  nav.v-navigation-drawer,
  header.v-toolbar,
  button.v-btn,
  div.v-card {
    display: none;
    border: none;
  }

  @page {
    margin: 0;
  }

  body {
    margin: 1.6cm;
  }

  .hideOnPrint {
    display: none;
  }
}
</style>
